import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useUserStore } from "store";
import { getCsrfToken } from "../../utils";
import { can } from "../../utils/access";

const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      value: `${date.getFullYear()}-${date.getMonth() + 1}`,
      label: `${date.getMonth() + 1} / ${date.getFullYear()}`,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    });
  }
  return options;
};

// Accounting reports and accountant email settings. Moved verbatim from
// OrganizationAccountingTab when it became a two-sub-tab shell (Raportit / Integraatio).
function AccountingReportsSubTab({ t, onDirtyChange }) {
  const { currentOrganization } = useUserStore();
  // The files carry the month's turnover, so the download block follows Raporttien katselu.
  const canSeeReports = can(currentOrganization, "reports.view");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [organization, setOrganizationRaw] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  });
  const monthOptions = getMonthOptions();

  useEffect(() => {
    if (currentOrganization?.organization_id) {
      fetchOrganization();
    }
  }, [currentOrganization?.organization_id]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/organizations/${currentOrganization.organization_id}/`,
        { headers: { "X-Organization-Id": currentOrganization.organization_id } }
      );
      setOrganizationRaw(response.data);
      setIsDirty(false);
    } catch (error) {
      console.error("Failed to fetch organization:", error);
      setSnackbar({ open: true, message: t("Failed to load organization data"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const setOrganization = useCallback((updater) => {
    setOrganizationRaw(updater);
    setIsDirty(true);
  }, []);

  const handleChange = (field) => (event) => {
    setOrganization((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSwitchChange = (field) => (event) => {
    setOrganization((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.patch(
        `/api/organizations/${organization.id}/`,
        {
          accountant_email: organization.accountant_email,
          send_monthly_invoice_summary: organization.send_monthly_invoice_summary,
          send_monthly_procountor_csv: organization.send_monthly_procountor_csv,
          send_monthly_netvisor_xml: organization.send_monthly_netvisor_xml,
          send_monthly_merged_pdf: organization.send_monthly_merged_pdf,
          send_monthly_accounting_report_pdf: organization.send_monthly_accounting_report_pdf,
          send_monthly_cash_register_pdf: organization.send_monthly_cash_register_pdf,
          send_monthly_receivables_pdf: organization.send_monthly_receivables_pdf,
          send_monthly_receivables_xlsx: organization.send_monthly_receivables_xlsx,
          send_monthly_purchase_invoices_zip: organization.send_monthly_purchase_invoices_zip,
          procountor_cash_card_handling: organization.procountor_cash_card_handling,
        },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      setIsDirty(false);
      setSnackbar({ open: true, message: t("Settings saved"), severity: "success" });
    } catch (error) {
      console.error("Failed to update settings:", error);
      const message = error.response?.data?.detail || t("Failed to save settings");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = isDirty;

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(hasChanges, () => handleSaveRef.current());
  }, [hasChanges, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSelectedYearMonth = () => {
    const [year, month] = selectedMonth.split("-").map(Number);
    return { year, month };
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await axios.get(url, {
        headers: { "X-Organization-Id": currentOrganization.organization_id },
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download:", error);
      setSnackbar({ open: true, message: t("Failed to download file"), severity: "error" });
    }
  };

  const handleSendToAccountant = async () => {
    if (!organization.accountant_email) {
      setSnackbar({ open: true, message: t("Please fill in accountant email first"), severity: "error" });
      return;
    }
    try {
      setSending(true);
      const { year, month } = getSelectedYearMonth();
      await axios.post(
        `/api/invoices/send-summary-to-accountant/`,
        { year, month },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      setSnackbar({ open: true, message: t("Summary sent to accountant successfully"), severity: "success" });
    } catch (error) {
      console.error("Failed to send summary:", error);
      const message = error.response?.data?.error || t("Failed to send summary to accountant");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organization) {
    return <Alert severity="error">{t("Failed to load organization data")}</Alert>;
  }

  const summaryEnabled = organization.send_monthly_invoice_summary;

  const renderToggle = (field, label, helperText) => (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, ml: 4 }}>
        <Box sx={{ flex: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={organization[field] || false}
                onChange={handleSwitchChange(field)}
              />
            }
            label={t(label)}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t(helperText)}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Accountant Email")}
            type="email"
            value={organization.accountant_email || ""}
            onChange={handleChange("accountant_email")}
            size="small"
            placeholder="kirjanpito@tilitoimisto.fi"
            helperText={t("Email address of your accountant")}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={summaryEnabled || false}
                    onChange={handleSwitchChange("send_monthly_invoice_summary")}
                    disabled={!organization.accountant_email}
                  />
                }
                label={t("Send Monthly Invoice Summary")}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                {organization.accountant_email
                  ? t("Automatically send monthly invoice summary to accountant on the 1st of each month")
                  : t("Fill in accountant email to enable monthly invoice summary")}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {summaryEnabled && (
          <>
            {renderToggle("send_monthly_procountor_csv", "Include Procountor CSV Export", "Include Procountor-compatible CSV with the monthly invoice summary")}
            {renderToggle("send_monthly_netvisor_xml", "Include Netvisor XML Export", "Include Netvisor-compatible Finvoice XML with the monthly invoice summary")}
            {renderToggle("send_monthly_merged_pdf", "Include Merged Invoice PDF", "Include all invoices merged into one PDF file with the monthly summary")}
            {renderToggle("send_monthly_accounting_report_pdf", "Include Accounting Report PDF", "Include accounting report PDF with myyntireskontra with the monthly summary")}
            {renderToggle("send_monthly_cash_register_pdf", "Include Cash Register Report PDF", "Include cash register report PDF with the monthly summary")}
            {renderToggle("send_monthly_receivables_pdf", "Include Open Invoices and Payments PDF", "Include the open invoices on the month's last day and the payments received during the month as PDF with the monthly summary")}
            {renderToggle("send_monthly_receivables_xlsx", "Include Open Invoices and Payments Excel", "Include the open invoices on the month's last day and the payments received during the month as Excel with the monthly summary")}
            {currentOrganization?.purchase_ledger_available === true &&
              renderToggle("send_monthly_purchase_invoices_zip", "Include purchase invoices ZIP", "Include the previous month's purchase invoices (CSV, Excel, PDF and XML) with the monthly summary")}
          </>
        )}

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("Cash/card/financing sales in Procountor file")}</InputLabel>
            <Select
              value={organization.procountor_cash_card_handling || "exclude"}
              onChange={handleChange("procountor_cash_card_handling")}
              label={t("Cash/card/financing sales in Procountor file")}
            >
              <MenuItem value="exclude">{t("Exclude from file (use cash register report)")}</MenuItem>
              <MenuItem value="paid">{t("Include as paid (Maksettu muualla)")}</MenuItem>
              <MenuItem value="open">{t("Include as open invoices")}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("How already-paid cash, card and financing sales appear in the Procountor invoice export. Bank-transfer invoices are always included. Excluding avoids double-counting if you also book the cash register report. Confirm with your accountant.")}
          </Typography>
        </Grid>

        {canSeeReports && (
          <Grid item xs={12}>
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                {t("Download or Send Report")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t("Month")}</InputLabel>
                  <Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    label={t("Month")}
                  >
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/invoices/summary-report-preview/?year=${year}&month=${month}`,
                      `myyntikooste_${year}_${String(month).padStart(2, "0")}.pdf`
                    );
                  }}
                >
                  {t("Download PDF Summary")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/invoices/summary-csv-download/?year=${year}&month=${month}`,
                      `myyntireskontra_${year}_${String(month).padStart(2, "0")}.csv`
                    );
                  }}
                >
                  {t("Download CSV Summary")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/reports/accounting/export/?export_format=pdf&year=${year}&month=${month}`,
                      `kirjanpitoraportti_${year}_${String(month).padStart(2, "0")}.pdf`
                    );
                  }}
                >
                  {t("Download Accounting Report PDF")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
                    const lastDay = new Date(year, month, 0).getDate();
                    const endDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
                    handleDownload(
                      `/api/reports/cash-register/export/?export_format=pdf&start_date=${startDate}&end_date=${endDate}`,
                      `kassaraportti_${year}_${String(month).padStart(2, "0")}.pdf`
                    );
                  }}
                >
                  {t("Download Cash Register PDF")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  data-testid="download-receivables-pdf"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/reports/receivables/export/?export_format=pdf&year=${year}&month=${month}`,
                      `avoimet_laskut_ja_suoritukset_${year}_${String(month).padStart(2, "0")}.pdf`
                    );
                  }}
                >
                  {t("Download Open Invoices and Payments PDF")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  data-testid="download-receivables-xlsx"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/reports/receivables/export/?export_format=xlsx&year=${year}&month=${month}`,
                      `avoimet_laskut_ja_suoritukset_${year}_${String(month).padStart(2, "0")}.xlsx`
                    );
                  }}
                >
                  {t("Download Open Invoices and Payments Excel")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/invoices/procountor-csv-download/?year=${year}&month=${month}`,
                      `procountor_laskut_${year}_${String(month).padStart(2, "0")}.csv`
                    );
                  }}
                >
                  {t("Download Procountor CSV")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/invoices/netvisor-xml-download/?year=${year}&month=${month}`,
                      `netvisor_${year}_${String(month).padStart(2, "0")}.xml`
                    );
                  }}
                >
                  {t("Download Netvisor XML")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/invoices/merged-invoices-pdf-download/?year=${year}&month=${month}`,
                      `laskut_${year}_${String(month).padStart(2, "0")}.pdf`
                    );
                  }}
                >
                  {t("Download All Invoices PDF")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const { year, month } = getSelectedYearMonth();
                    handleDownload(
                      `/api/invoices/accounting-zip-download/?year=${year}&month=${month}`,
                      `kirjanpito_${year}_${String(month).padStart(2, "0")}.zip`
                    );
                  }}
                >
                  {t("Download All as ZIP")}
                </Button>
              </Box>
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSendToAccountant}
                  disabled={sending || !organization.accountant_email}
                >
                  {sending ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  {t("Send to Accountant")}
                </Button>
                {!organization.accountant_email && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("Please fill in accountant email first")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(AccountingReportsSubTab);
